import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-beta-hcg"),
  kind: "test",
  tags: [
    "lab",
    "her",
    "post-cycle",
    "testing",
    "ivf",
    "iui",
    "fet",
    "emotional",
  ],
  title: "Beta hCG",
  subtitle: "Pregnancy blood test to detect hCG levels",
  body: {
    testName: "Beta hCG (Pregnancy Blood Test)",
    category: "blood-marker",
    whatItMeasures:
      "Measures human chorionic gonadotropin (hCG), a hormone produced by pregnancy tissue. A beta hCG result is interpreted with the treatment timing, symptoms, repeat results, and ultrasound when appropriate; one result cannot by itself establish a pregnancy's location or viability.",
    howItWorks:
      "A small blood sample is drawn from a vein and measured by the laboratory. Your clinic sets the draw date and may request repeat testing based on the treatment protocol and first result. A single threshold or rate of change is not a diagnosis: serial values, ultrasound, symptoms, and examination may all matter. Do not use a beta result to decide whether to stop or change medication; follow the plan from your care team and seek urgent advice for severe pain, fainting, shoulder pain, or heavy bleeding.",
    quickFacts: [
      { kind: "sample", value: "Blood" },
      { kind: "time", value: "5 min draw" },
      { kind: "pain", value: "Needle pinch" },
      { kind: "prep", value: "Follow your clinic's medication instructions" },
      { kind: "results", value: "Same day, often a few hours" },
    ],
    turnaroundTime: "Same day (a few hours)",
    costRange: { low: 30, high: 150 },
    atHome: false,
    clinicRequired: true,
    cycleDay: "Timing is set by your clinic's protocol after treatment",
    markerTiming: [
      {
        marker: "Beta hCG",
        timing:
          "Timing and any repeat draw are set by your clinic's protocol and the result",
        reason:
          "hCG timing and interpretation depend on the treatment date, assay, repeat results, symptoms, and clinical assessment.",
      },
    ],
    dataPoints: [makeId("dp-beta-hcg-level")],
    section: "implantation",
  },
  sources: [
    {
      title: "ACOG Practice Bulletin No. 193: Tubal Ectopic Pregnancy",
      authors:
        "American College of Obstetricians and Gynecologists' Committee on Practice Bulletins—Gynecology",
      journal: "Obstetrics & Gynecology",
      year: 2018,
      url: "https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2018/03/tubal-ectopic-pregnancy",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 3,
};

registerBlock(test);
